import { useState, useEffect } from 'react';
import { useApi } from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';
import { useTheme } from '../../contexts/ThemeContext';
import ColorPicker from '../../components/ColorPicker';
import LogoUploader from '../../components/LogoUploader';
import ThemeSelector from '../../components/ThemeSelector';
import { ThemePreset } from '../../config/themePresets';

const FONT_OPTIONS = [
    'Inter',
    'Roboto',
    'Poppins',
    'Montserrat',
    'Open Sans',
    'Lato',
    'Raleway',
    'Work Sans',
    'Nunito',
    'Source Sans Pro',
];

export default function BrandingSettings() {
    const api = useApi();
    const { tenant } = useTenant();
    const theme = useTheme();

    const [formData, setFormData] = useState({
        logoUrl: tenant?.logoUrl || '',
        primaryColor: tenant?.primaryColor || '#3b82f6',
        secondaryColor: tenant?.secondaryColor || '#8b5cf6',
        backgroundColor: tenant?.backgroundColor || '#ffffff',
        accentColor: tenant?.accentColor || '#3b82f6',
        fontFamily: tenant?.fontFamily || 'Inter',
    });

    const [saving, setSaving] = useState(false);
    const [previewMode, setPreviewMode] = useState(true);

    // Apply preview changes in real-time
    useEffect(() => {
        if (previewMode) {
            theme.applyTheme(formData);
        }
    }, [formData, previewMode]);

    const handleSave = async () => {
        try {
            setSaving(true);
            await api.updateTheme(formData);
            alert('Theme updated successfully!');
            // Reload page to fetch updated tenant data
            window.location.reload();
        } catch (error) {
            console.error('Failed to update theme:', error);
            alert('Failed to update theme. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        const defaults = {
            logoUrl: tenant?.logoUrl || '',
            primaryColor: tenant?.primaryColor || '#3b82f6',
            secondaryColor: tenant?.secondaryColor || '#8b5cf6',
            backgroundColor: tenant?.backgroundColor || '#ffffff',
            accentColor: tenant?.accentColor || '#3b82f6',
            fontFamily: tenant?.fontFamily || 'Inter',
        };
        setFormData(defaults);
    };

    const handlePresetSelect = (preset: ThemePreset) => {
        setFormData(prev => ({
            ...prev,
            ...preset.colors,
        }));
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Settings Panel */}
            <div className="space-y-6">
                <div>
                    <h1 className="text-3xl font-bold mb-2 text-text-primary">Branding & Theme</h1>
                    <p className="text-text-secondary">
                        Customize your company's branding and color scheme
                    </p>
                </div>

                <div className="bg-surface rounded-lg shadow p-6 space-y-6 border border-border">
                    <LogoUploader
                        currentLogo={formData.logoUrl}
                        onUpload={(logo) => setFormData({ ...formData, logoUrl: logo })}
                    />

                    {/* Theme Presets */}
                    <div className="border-t pt-6">
                        <ThemeSelector onSelectPreset={handlePresetSelect} showPreview={false} />
                    </div>

                    <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold mb-4">Custom Colors</h3>
                        <div className="space-y-4">
                            <ColorPicker
                                label="Primary Color"
                                value={formData.primaryColor}
                                onChange={(color) => setFormData({ ...formData, primaryColor: color })}
                            />
                            <ColorPicker
                                label="Secondary Color"
                                value={formData.secondaryColor}
                                onChange={(color) => setFormData({ ...formData, secondaryColor: color })}
                            />
                            <ColorPicker
                                label="Background Color"
                                value={formData.backgroundColor}
                                onChange={(color) => setFormData({ ...formData, backgroundColor: color })}
                                presets={['#ffffff', '#f9fafb', '#f3f4f6', '#1f2937', '#111827', '#0f172a']}
                            />
                            <ColorPicker
                                label="Accent Color"
                                value={formData.accentColor}
                                onChange={(color) => setFormData({ ...formData, accentColor: color })}
                            />
                        </div>
                    </div>

                    <div className="border-t pt-6">
                        <h3 className="text-lg font-semibold mb-4 text-text-primary">Typography</h3>
                        <div>
                            <label className="block text-sm font-medium text-text-secondary mb-2">
                                Font Family
                            </label>
                            <select
                                value={formData.fontFamily}
                                onChange={(e) => setFormData({ ...formData, fontFamily: e.target.value })}
                                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-surface text-text-primary"
                            >
                                {FONT_OPTIONS.map((font) => (
                                    <option key={font} value={font} style={{ fontFamily: font }}>
                                        {font}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="border-t pt-6 flex space-x-3">
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                            onClick={handleReset}
                            className="px-4 py-2 border border-border rounded-lg hover:bg-background text-text-secondary hover:text-text-primary transition"
                        >
                            Reset
                        </button>
                    </div>

                    <div className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id="preview"
                            checked={previewMode}
                            onChange={(e) => setPreviewMode(e.target.checked)}
                            className="rounded"
                        />
                        <label htmlFor="preview" className="text-sm text-text-secondary">
                            Enable live preview
                        </label>
                    </div>
                </div>
            </div>

            {/* Live Preview Panel */}
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold mb-2 text-text-primary">Live Preview</h2>
                    <p className="text-text-secondary">
                        See how your changes will look
                    </p>
                </div>

                <div
                    className="rounded-lg shadow p-6 space-y-4"
                    style={{
                        backgroundColor: formData.backgroundColor,
                        color: formData.backgroundColor === '#ffffff' || formData.backgroundColor === '#f9fafb' || formData.backgroundColor === '#f3f4f6' ? '#111827' : '#ffffff'
                    }}
                >
                    {/* Logo Preview */}
                    {formData.logoUrl && (
                        <div className="border-b pb-4" style={{ borderColor: formData.primaryColor + '40' }}>
                            <img
                                src={formData.logoUrl}
                                alt="Logo"
                                className="h-12 w-auto"
                            />
                        </div>
                    )}

                    {/* Sample UI Elements */}
                    <div className="space-y-4">
                        <h3 className="text-xl font-bold" style={{ fontFamily: formData.fontFamily }}>
                            Welcome to {tenant?.name || 'Your Company'}
                        </h3>

                        <p style={{
                            fontFamily: formData.fontFamily,
                            opacity: 0.8
                        }}>
                            This is how your text will appear with the selected font and colors.
                        </p>

                        <div className="flex flex-wrap gap-2">
                            <button
                                className="px-4 py-2 rounded-lg text-white font-medium"
                                style={{ backgroundColor: formData.primaryColor, fontFamily: formData.fontFamily }}
                            >
                                Primary Button
                            </button>
                            <button
                                className="px-4 py-2 rounded-lg text-white font-medium"
                                style={{ backgroundColor: formData.secondaryColor, fontFamily: formData.fontFamily }}
                            >
                                Secondary Button
                            </button>
                            <button
                                className="px-4 py-2 rounded-lg text-white font-medium"
                                style={{ backgroundColor: formData.accentColor, fontFamily: formData.fontFamily }}
                            >
                                Accent Button
                            </button>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div
                                className="p-4 rounded-lg"
                                style={{
                                    backgroundColor: formData.primaryColor + '20',
                                    color: formData.primaryColor,
                                    fontFamily: formData.fontFamily,
                                }}
                            >
                                <div className="text-2xl font-bold">42</div>
                                <div className="text-sm">Active Games</div>
                            </div>
                            <div
                                className="p-4 rounded-lg"
                                style={{
                                    backgroundColor: formData.accentColor + '20',
                                    color: formData.accentColor,
                                    fontFamily: formData.fontFamily,
                                }}
                            >
                                <div className="text-2xl font-bold">128</div>
                                <div className="text-sm">Employees</div>
                            </div>
                        </div>

                        <div
                            className="border rounded-lg p-4"
                            style={{
                                fontFamily: formData.fontFamily,
                                borderColor: formData.primaryColor + '40',
                                backgroundColor: formData.backgroundColor === '#ffffff' ? '#f9fafb' : formData.backgroundColor + 'dd'
                            }}
                        >
                            <h4 className="font-semibold mb-2">Sample Card</h4>
                            <p className="text-sm mb-3" style={{ opacity: 0.8 }}>
                                This is how cards and content will appear in your application.
                            </p>
                            <a href="#" style={{ color: formData.primaryColor, fontWeight: 500 }}>
                                Learn more →
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
